import { useState, useEffect } from 'react';
import { useApi } from '@/hooks/useApi';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Link2, DollarSign, Truck, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { DELIVERY_TYPE_LABELS } from '@/types/pricing.types';

interface Connection {
  id: string;
  carrier_id: string;
  is_default: boolean;
  is_active: boolean;
  connected_at: string;
  pricing_list_id: string | null;
  carrier?: {
    id: string;
    company_name?: string;
    name?: string;
  };
  pricing_list?: {
    id: string;
    name: string;
    default_delivery_fee: number;
    default_service_type?: {
      code: string;
      name: string;
    };
  } | null;
}

interface PricingListOption {
  id: string;
  name: string;
  default_delivery_fee: number;
  default_service_type?: {
    code: string;
    name: string;
  } | null;
}

interface TenantConnectionCardProps {
  tenantId: string;
}

export function TenantConnectionCard({ tenantId }: TenantConnectionCardProps) {
  const { get, loading } = useApi();
  const { patch } = useApi();
  const [connection, setConnection] = useState<Connection | null>(null);
  const [pricingLists, setPricingLists] = useState<PricingListOption[]>([]);
  const [selectedPricingListId, setSelectedPricingListId] = useState<string>('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchConnection();
    fetchPricingLists();
  }, [tenantId]);

  const fetchConnection = async () => {
    try {
      const result: any = await get(`/api/v1/dashboard/tenants/${tenantId}/connection`);
      const data = result?.data || result;
      // For carrier role, result is a single connection; for admin it's an array
      const conn = Array.isArray(data) ? data[0] : data;
      if (conn) {
        setConnection(conn);
        setSelectedPricingListId(conn.pricing_list_id || '');
      }
    } catch (error) {
      console.error('Failed to fetch connection:', error);
    }
  };

  const fetchPricingLists = async () => {
    try {
      const result: any = await get('/api/v1/carriers/pricing-lists?limit=100');
      const data = result?.data || result;
      const list = data?.list || data?.data || (Array.isArray(data) ? data : []);
      setPricingLists(list);
    } catch (error) {
      console.error('Failed to fetch pricing lists:', error);
    }
  };

  const handleSavePricingList = async () => {
    if (!connection) return;

    setSaving(true);
    try {
      await patch(`/api/v1/dashboard/tenants/${tenantId}/connection/${connection.id}`, {
        pricing_list_id: selectedPricingListId || null,
      });
      toast.success('تم تحديث قائمة الأسعار');
      fetchConnection();
    } catch (error) {
      toast.error('فشل في تحديث قائمة الأسعار');
    } finally {
      setSaving(false);
    }
  };

  const hasChanged = connection?.pricing_list_id !== selectedPricingListId &&
    !(connection?.pricing_list_id === null && selectedPricingListId === '');

  if (loading && !connection) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground text-sm">
          جاري تحميل بيانات العقد...
        </CardContent>
      </Card>
    );
  }

  if (!connection) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Link2 className="h-4 w-4" />
            العقد والاتصال
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">لا يوجد اتصال نشط مع هذا العميل</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Link2 className="h-4 w-4" />
          العقد والاتصال
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Connection Info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="flex items-center gap-2 text-sm">
            <Truck className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">شركة الشحن:</span>
            <span className="font-medium">
              {connection.carrier?.company_name || connection.carrier?.name || '-'}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">الحالة:</span>
            <Badge variant={connection.is_active ? 'default' : 'secondary'}>
              {connection.is_active ? 'نشط' : 'غير نشط'}
            </Badge>
            {connection.is_default && (
              <Badge variant="outline">افتراضي</Badge>
            )}
          </div>
        </div>

        {/* Current Pricing List */}
        {connection.pricing_list && (
          <div className="rounded-lg border bg-muted/30 p-3 space-y-1">
            <div className="flex items-center gap-2 text-sm font-medium">
              <DollarSign className="h-4 w-4 text-primary" />
              قائمة الأسعار الحالية: {connection.pricing_list.name}
            </div>
            <div className="flex gap-4 text-xs text-muted-foreground">
              <span>السعر الافتراضي: {connection.pricing_list.default_delivery_fee}</span>
              {connection.pricing_list.default_service_type && (
                <span>
                  نوع التوصيل: {DELIVERY_TYPE_LABELS[connection.pricing_list.default_service_type.code] || connection.pricing_list.default_service_type.name}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Change Pricing List */}
        <div className="space-y-2">
          <label className="text-sm font-medium">تغيير قائمة الأسعار</label>
          <div className="flex items-center gap-2">
            <Select
              value={selectedPricingListId}
              onValueChange={setSelectedPricingListId}
            >
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="اختر قائمة أسعار" />
              </SelectTrigger>
              <SelectContent>
                {pricingLists.map((pl) => (
                  <SelectItem key={pl.id} value={pl.id}>
                    {pl.name} ({pl.default_delivery_fee})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              onClick={handleSavePricingList}
              disabled={!hasChanged || saving}
              className="gap-1.5"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              حفظ
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
